import Phaser from 'phaser';
import { GLYPH_SIZE, getWord, getGlyphColor } from '../toki/TokiPonaData';
import type { TokiPonaAudio, AudioChannel } from '../toki/TokiPonaAudio';
import { VOICE_PROFILES } from '../toki/TokiPonaAudio';

// "sina kama tawa pini ike anu seme" — have you come to a bad end, or what?
const DEATH_GLYPHS = [94, 22, 105, 80, 13, 7, 89];

const GLYPH_SCALE = 1;
const GAP = 1; // 1px horizontal buffer between glyphs
const OVERLAP_MS = 250; // crossfade overlap between loop iterations

export class DeathScreenScene extends Phaser.Scene {
    private onDismiss: (() => void) | null = null;
    private dismissed = false;
    private musicInstances: Phaser.Sound.BaseSound[] = [];
    private loopTimer: Phaser.Time.TimerEvent | null = null;
    private tokiAudio: TokiPonaAudio | null = null;
    private speechChannel: AudioChannel | null = null;
    private glyphImages: Phaser.GameObjects.Image[] = [];
    private currentWordIndex = -1;
    private musicPlayCount = 0;

    constructor() {
        super({ key: 'DeathScreenScene' });
    }

    init(data: { onDismiss?: () => void; audio?: TokiPonaAudio }): void {
        this.onDismiss = data.onDismiss || null;
        this.dismissed = false;
        this.tokiAudio = data.audio || null;
        this.speechChannel = null;
        this.glyphImages = [];
        this.currentWordIndex = -1;
    }

    create(): void {
        const { width, height } = this.cameras.main;

        // Full black overlay
        const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 1);
        overlay.setOrigin(0, 0).setDepth(0);

        // Center the glyph row
        const glyphSize = GLYPH_SIZE * GLYPH_SCALE;
        const totalWidth = DEATH_GLYPHS.length * glyphSize + (DEATH_GLYPHS.length - 1) * GAP * GLYPH_SCALE;
        const startX = (width - totalWidth) / 2;
        const y = height / 2;

        for (let i = 0; i < DEATH_GLYPHS.length; i++) {
            const idx = DEATH_GLYPHS[i];
            const x = startX + i * (glyphSize + GAP * GLYPH_SCALE) + glyphSize / 2;
            const glyph = this.add.image(x, y, 'toki_glyphs', idx);
            glyph.setScale(GLYPH_SCALE);
            glyph.setTint(0xffffff);
            glyph.setDepth(1);
            this.glyphImages.push(glyph);
        }

        // Play death music with overlapping loop
        if (this.cache.audio.exists('death_music')) {
            this.playMusicLoop();
        }

        // Read out the death message with olin voice
        this.playSpeech();

        // Dismiss on any key
        this.input.keyboard!.on('keydown', () => {
            this.dismiss();
        });

        // Dismiss on any click/touch
        this.input.on('pointerdown', () => {
            this.dismiss();
        });
    }

    private playMusicLoop(): void {
        if (this.musicPlayCount >= 3) return;

        const volume = 0.2 * Math.pow(0.5, this.musicPlayCount);
        this.musicPlayCount++;

        const instance = this.sound.add('death_music');
        this.musicInstances.push(instance);
        instance.play({ volume });

        // Schedule the next overlapping play 250ms before this one ends
        const duration = (instance as any).duration as number; // seconds
        if (duration > 0 && this.musicPlayCount < 3) {
            const delayMs = Math.max(0, duration * 1000 - OVERLAP_MS);
            this.loopTimer = this.time.delayedCall(delayMs, () => {
                if (!this.dismissed) {
                    this.playMusicLoop();
                }
            });
        }
    }

    private async playSpeech(): Promise<void> {
        if (!this.tokiAudio) return;

        // Wait 4 seconds before reading the message
        await new Promise(r => setTimeout(r, 1250));
        if (this.dismissed) return;

        // Retry channel creation a few times in case decode is in progress
        for (let attempt = 0; attempt < 3 && !this.speechChannel && !this.dismissed; attempt++) {
            this.speechChannel = this.tokiAudio.createChannel(VOICE_PROFILES.olin) ?? null;
            if (!this.speechChannel) await new Promise(r => setTimeout(r, 150));
        }
        if (!this.speechChannel || this.dismissed) return;

        for (let i = 0; i < DEATH_GLYPHS.length; i++) {
            if (this.dismissed) return;

            // Highlight current glyph
            if (this.currentWordIndex >= 0 && this.currentWordIndex < this.glyphImages.length) {
                this.glyphImages[this.currentWordIndex].setTint(0xffffff);
            }
            this.currentWordIndex = i;
            this.glyphImages[i].setTint(getGlyphColor(DEATH_GLYPHS[i]));

            const wordStr = getWord(DEATH_GLYPHS[i]);
            if (!wordStr) continue;

            try {
                await this.speechChannel.play(wordStr, '0.5');
            } catch {
                await new Promise(r => setTimeout(r, 200));
            }
        }

        // Return last glyph to white
        if (!this.dismissed && this.currentWordIndex >= 0 && this.currentWordIndex < this.glyphImages.length) {
            this.glyphImages[this.currentWordIndex].setTint(0xffffff);
            this.currentWordIndex = -1;
        }
    }

    dismiss(): void {
        if (this.dismissed) return;
        this.dismissed = true;
        if (this.loopTimer) {
            this.loopTimer.destroy();
            this.loopTimer = null;
        }
        if (this.speechChannel) {
            this.speechChannel.stop();
            this.speechChannel = null;
        }
        for (const inst of this.musicInstances) {
            inst.stop();
            inst.destroy();
        }
        this.musicInstances = [];
        this.onDismiss?.();
        this.scene.stop();
    }
}
